/* sse.ts — lector de Server-Sent Events sobre POST.
 *
 * `EventSource` no sirve aquí: solo hace GET y no manda cuerpo, y los dos flujos de Vigía
 * (`/api/chat/stream` y `/api/analizar-proceso`) reciben su entrada por POST. Así que se lee el
 * `ReadableStream` de la respuesta a mano.
 *
 * El formato del servidor es siempre `data: {json}\n\n` — una línea de datos por evento, sin
 * `event:` ni `id:`. Se acepta igualmente el troceado arbitrario de la red: un evento puede
 * llegar partido en varios trozos y varios eventos pueden llegar en el mismo.
 */
import { abrirFlujo, ErrorApi } from './cliente'

/** Recorre los eventos del flujo según van llegando. Se corta con el `AbortSignal`. */
export async function* flujoEventos<T>(
  ruta: string,
  cuerpo: unknown,
  señal?: AbortSignal,
): AsyncGenerator<T, void, undefined> {
  const respuesta = await abrirFlujo(ruta, cuerpo, señal)
  const flujo = respuesta.body
  if (!flujo) throw new ErrorApi(0, 'El servidor no devolvió un flujo de eventos.')

  const lector = flujo.pipeThrough(new TextDecoderStream()).getReader()
  let pendiente = ''
  try {
    for (;;) {
      const { done, value } = await lector.read()
      if (done) break
      pendiente += value

      // Un evento termina en línea en blanco. Se procesan todos los completos y se guarda el
      // resto: partir por `\n` a secas mezclaría eventos cuando la red trocea a mitad de línea.
      for (;;) {
        const fin = finDeEvento(pendiente)
        if (!fin) break
        const evento = parsear<T>(pendiente.slice(0, fin.desde))
        pendiente = pendiente.slice(fin.hasta)
        if (evento !== undefined) yield evento
      }
    }
    // Cierre sin línea en blanco final: el último evento sigue siendo válido.
    const ultimo = parsear<T>(pendiente)
    if (ultimo !== undefined) yield ultimo
  } finally {
    // Si quien consume abandona el bucle (un `break`, o el componente se desmonta), hay que
    // soltar el lector o la conexión queda abierta y el servidor sigue gastando tokens.
    await lector.cancel().catch(() => {})
  }
}

/**
 * Dónde termina el primer evento completo de `texto`, o `null` si todavía no llegó entero.
 *
 * Nuestro servidor emite `\n\n`, pero el estándar de SSE acepta también `\r\n\r\n` y `\r\r`, y
 * cualquier intermediario podría normalizar los finales de línea. Buscar solo `\n\n` haría que el
 * flujo se quedara mudo para siempre sin un error: los bytes llegan y ningún evento se emite.
 */
function finDeEvento(texto: string): { desde: number; hasta: number } | null {
  let mejor: { desde: number; hasta: number } | null = null
  for (const sep of ['\r\n\r\n', '\n\n', '\r\r']) {
    const i = texto.indexOf(sep)
    if (i !== -1 && (mejor === null || i < mejor.desde)) {
      mejor = { desde: i, hasta: i + sep.length }
    }
  }
  return mejor
}

function parsear<T>(bruto: string): T | undefined {
  const datos = bruto
    .split(/\r\n|\n|\r/)
    .filter((l) => l.startsWith('data:'))
    .map((l) => l.slice(5).trimStart())
    .join('\n')
  if (!datos) return undefined
  try {
    return JSON.parse(datos) as T
  } catch {
    // Un evento ilegible no debe tumbar el turno entero: los demás siguen llegando.
    return undefined
  }
}
